import { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  deleteUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingRedirect, setProcessingRedirect] = useState(true);
  const userDocSeenRef = useRef(false);
  const authReadyRef = useRef(false); // true after first onAuthStateChanged fires


  // Sign up function
  async function signup(email, password, displayName) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      displayName: displayName,
      role: 'user',
      createdAt: new Date().toISOString(),
      calendars: [],
      favoriteCalendars: []
    });
    
    return userCredential;
  }

  // Login function
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Google login — tries popup first, falls back to redirect on mobile
  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName,
          role: 'user',
          createdAt: new Date().toISOString(),
          calendars: [],
          favoriteCalendars: []
        });
      }

      return result;
    } catch (error) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile && (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request')) {
        await signInWithRedirect(auth, provider);
      } else {
        throw error;
      }
    }
  }

  // Logout function
  function logout() {
    return signOut(auth);
  }

  // Delete account function
  async function deleteAccount() {
    if (auth.currentUser) {
      await deleteUser(auth.currentUser);
    }
  }

  // Handle redirect result (for mobile Google login via signInWithRedirect)
  useEffect(() => {
    async function handleRedirect() {
      try {
        const result = await getRedirectResult(auth);

        if (result && result.user) {
          const user = result.user;
          let userDoc = await getDoc(doc(db, 'users', user.uid));

          if (!userDoc.exists()) {
            await setDoc(doc(db, 'users', user.uid), {
              email: user.email,
              displayName: user.displayName,
              role: 'user',
              createdAt: new Date().toISOString(),
              calendars: [],
              favoriteCalendars: []
            });

            // Wait briefly then verify
            await new Promise(resolve => setTimeout(resolve, 1000));
            userDoc = await getDoc(doc(db, 'users', user.uid));
            if (!userDoc.exists()) {
              console.error('Failed to create user document after redirect');
            }
          }
        }
      } catch (error) {
        console.error('Redirect sign-in error:', error.code, error.message);
      } finally {
        setProcessingRedirect(false);
      }
    }

    handleRedirect();
  }, []);

  // Auth state listener — waits for Firebase to restore session before rendering
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      } else {
        setUserRole(null);
      }

      authReadyRef.current = true; // Firebase init complete — safe to act on auth state
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Monitor user document changes in real-time (deletion and role changes)
  useEffect(() => {
    if (!currentUser) return;

    userDocSeenRef.current = false;

    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          // Double guard: only auto-logout after both:
          // 1. Firebase auth is fully initialized (authReadyRef)
          // 2. We confirmed the user doc existed before (userDocSeenRef)
          // This prevents premature logout during the initialization phase.
          if (authReadyRef.current && userDocSeenRef.current) {
            logout().catch((error) => {
              console.error('Error during auto-logout:', error);
            });
          }
        } else {
          userDocSeenRef.current = true;
          setUserRole(docSnapshot.data().role);
        }
      },
      (error) => {
        console.error('Error monitoring user document:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    currentUser,
    userRole,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    deleteAccount,
    processingRedirect
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="auth-init-loader">
          <div className="spinner"></div>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}